import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Chip,
  Button,
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import axios from 'axios';
import envConfig from '../../../bin/env.config';
import { useSelector } from 'react-redux';
import {
  getHealthStatusStyle,
  formatHealthDate,
  buildOodReportUrl,
  formatScopeRatio,
} from './projectHealthConfig';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '1.25rem',
    lineHeight: 1.3,
  },
  metricSub: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  alertsRow: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  contextNote: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },
  clickableMetric: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    '&:hover': {
      color: '#3B82F6',
    },
  },
  popoverPaper: {
    padding: 16,
    maxWidth: 320,
  },
}));

function MetricBlock({ title, children, classes }) {
  return (
    <div>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      {children}
    </div>
  );
}

function formatCityLabel(city) {
  return city.province ? `${city.city}, ${city.province}` : city.city;
}

export default function ProjectHealthSummaryCard({ project }) {
  const classes = useStyles();
  const token = useSelector((state) => state.session.token);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [citiesAnchorEl, setCitiesAnchorEl] = useState(null);

  useEffect(() => {
    if (!project?.user_type_id || !project?.id) return;

    const fetchHealth = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/project-health-summary`,
          {
            params: {
              user_type_id: project.user_type_id,
              project_id: project.id,
              date_period: 14,
              include_delta: 1,
            },
            headers: { Authorization: token },
          }
        );
        setData(response.data.data);
      } catch (err) {
        setError('Failed to load project health summary');
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
  }, [project?.user_type_id, project?.id, token]);

  if (loading) {
    return (
      <Paper className={classes.root}>
        <div className={classes.loadingBox}>
          <CircularProgress size={32} />
        </div>
      </Paper>
    );
  }

  if (error || !data) {
    return (
      <Paper className={classes.root}>
        <Typography color="error">{error || 'No health data available'}</Typography>
        <Typography className={classes.contextNote}>
          Based on price freshness (14d) and product mapping. For matching progress and scope setup, see Summary on the project list.
        </Typography>
      </Paper>
    );
  }

  const statusStyle = getHealthStatusStyle(data.status);
  const cacheLabel = formatHealthDate(data.cache?.metrics_updated_at || data.cache?.ood_updated_at);
  const oodUrl = buildOodReportUrl(project.user_type_id, project.id);
  const citiesWithoutData = data.scope?.cities_without_data || [];
  const hasCityScopeGap = (data.scope?.configured_cities ?? 0) > (data.scope?.cities_with_data ?? 0);
  const hasMissingCities = hasCityScopeGap || citiesWithoutData.length > 0;

  const handleCitiesClick = (event) => {
    if (!hasMissingCities) return;
    setCitiesAnchorEl(event.currentTarget);
  };

  const handleCitiesClose = () => {
    setCitiesAnchorEl(null);
  };

  const alertParts = [];
  if (data.alerts?.problem_banner_count > 0) {
    alertParts.push(`${data.alerts.problem_banner_count} banner${data.alerts.problem_banner_count > 1 ? 's' : ''} >70% not updated`);
  }
  if (data.alerts?.problem_pmg_count > 0) {
    alertParts.push(`${data.alerts.problem_pmg_count} problem PMG row${data.alerts.problem_pmg_count > 1 ? 's' : ''}`);
  }
  if (data.trend?.not_updated_delta != null && data.trend.not_updated_delta !== 0) {
    const sign = data.trend.not_updated_delta > 0 ? '+' : '';
    alertParts.push(`${sign}${data.trend.not_updated_delta} not-updated vs previous cache`);
  }
  if (
    data.scope?.configured_banners > 0
    && data.scope.banners_with_data < data.scope.configured_banners
  ) {
    alertParts.push(
      `${data.scope.configured_banners - data.scope.banners_with_data} configured banner(s) without data`
    );
  }
  if (
    data.scope?.configured_cities > 0
    && data.scope.cities_with_data < data.scope.configured_cities
  ) {
    const cityNames = citiesWithoutData.map(formatCityLabel).join(', ');
    alertParts.push(
      cityNames
        ? `${data.scope.configured_cities - data.scope.cities_with_data} configured city/cities without data: ${cityNames}`
        : `${data.scope.configured_cities - data.scope.cities_with_data} configured city/cities without data`
    );
  }
  if (data.mapping?.unmapped_products > 0) {
    alertParts.push(
      `${data.mapping.unmapped_products} project product(s) without PMG mapping`
    );
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Box>
          <Typography variant="h6" component="h2">
            Project Health
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Cache: {cacheLabel} · {data.date_period_days}-day window
          </Typography>
        </Box>
        <Chip
          label={data.status_label || statusStyle.label}
          style={{
            color: statusStyle.color,
            backgroundColor: statusStyle.backgroundColor,
            fontWeight: 600,
          }}
        />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <MetricBlock title="Scope" classes={classes}>
            <Typography className={classes.metricValue}>
              {formatScopeRatio(data.scope?.banners_with_data, data.scope?.configured_banners)}
            </Typography>
            <Typography className={classes.metricSub}>banners with data</Typography>
            <Typography
              className={`${classes.metricSub}${hasMissingCities ? ` ${classes.clickableMetric}` : ''}`}
              onClick={handleCitiesClick}
              title={hasMissingCities ? 'Click to see cities without data' : undefined}
            >
              {formatScopeRatio(data.scope?.cities_with_data, data.scope?.configured_cities)} cities with data
            </Typography>
            <Typography className={classes.metricSub}>
              {data.scope?.stores ?? 0} stores in scope
            </Typography>
          </MetricBlock>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricBlock title={`Freshness (${data.date_period_days}d)`} classes={classes}>
            <Typography className={classes.metricValue} style={{ color: data.freshness?.updated_pct >= 70 ? '#22C55E' : '#EF4444' }}>
              {data.freshness?.updated_pct ?? 0}% updated
            </Typography>
            <Typography className={classes.metricSub}>{data.freshness?.not_updated_pct ?? 0}% not updated</Typography>
            <Typography className={classes.metricSub}>{data.freshness?.checked_coverage_pct ?? 0}% manually checked</Typography>
          </MetricBlock>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricBlock title="Catalog mapping" classes={classes}>
            <Typography className={classes.metricValue}>{data.mapping?.mapped_pct ?? 0}%</Typography>
            <Typography className={classes.metricSub}>
              {data.mapping?.products_with_pmg ?? 0} of {data.mapping?.total_products ?? 0} project products have PMG
            </Typography>
            <Typography className={classes.metricSub}>
              {data.mapping?.unmapped_products ?? 0} without PMG
            </Typography>
            <Typography className={classes.metricSub}>
              {data.mapping?.total_pmg_mappings ?? 0} PMG mappings
              {data.mapping?.total_products > 0
                ? ` (avg ${data.mapping?.avg_pmg_per_product ?? 0} per product)`
                : ''}
            </Typography>
          </MetricBlock>
        </Grid>
      </Grid>

      {alertParts.length > 0 && (
        <div className={classes.alertsRow}>
          <Typography variant="body2" color="textSecondary">
            <strong>Alerts:</strong> {alertParts.join(' · ')}
          </Typography>
          {data.alerts?.top_problem_banners?.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
              Worst banners: {data.alerts.top_problem_banners.map((b) => `${b.banner_name} (${b.not_updated_pct}%)`).join(', ')}
            </Typography>
          )}
        </div>
      )}

      <Typography className={classes.contextNote}>
        Based on price freshness ({data.date_period_days}d) and product mapping. For matching progress and scope setup, see{' '}
        <strong>Summary</strong> on the project list.
      </Typography>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          endIcon={<OpenInNewIcon />}
          onClick={() => window.open(oodUrl, '_blank')}
        >
          Open Not updated Report
        </Button>
      </div>

      <Popover
        open={Boolean(citiesAnchorEl)}
        anchorEl={citiesAnchorEl}
        onClose={handleCitiesClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Cities without data ({citiesWithoutData.length})
        </Typography>
        <List dense disablePadding>
          {citiesWithoutData.map((city) => (
            <ListItem key={`${city.city}-${city.province}`} disableGutters>
              <ListItemText primary={formatCityLabel(city)} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Paper>
  );
}
